import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Signal } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthentificationService } from '@core/services/authentification/authentification.service';
import { LinksService } from '@core/services/links/links.service';
import { UserService } from '@core/services/user/user.service';
import { environment } from '@environments/environment';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { PbdsHeaderShadowDirective } from '@pb-design-system/pb-design-system/header-shadow';
import { MenuItem } from '@shared/model/menu-item.type';
import { ModalService } from '@shared/services/modal.service';
import { DropdownMenuComponent } from '@ui/dropdown-menu/dropdown-menu.component';
import { IconComponent } from '@ui/icon/icon.component';
import { filter, map, Observable, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../base-component';
import { ImpersonationModalComponent } from '../impersonation-modal/impersonation-modal.component';
import { MenuService } from '../menu/services/menu.service';
import { VideoModalComponent } from '../video-modal/video-modal.component';
import { LanguageSwitcherComponent } from './language-switcher/language-switcher.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    NgbPopover,
    TranslatePipe,
    LanguageSwitcherComponent,
    DropdownMenuComponent,
    PbdsHeaderShadowDirective,
    MatMenuModule,
    AsyncPipe,
    IconComponent,
  ],
})
export class HeaderComponent extends BaseComponent implements OnInit {
  public readonly authentificationService = inject(AuthentificationService);
  public readonly userService = inject(UserService);
  private readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly linksService = inject(LinksService);
  protected showMenuToggler$: Observable<boolean>;
  protected readonly envName = environment.name?.substring(0, 3) || 'pro';
  protected readonly isMenuOpen = computed(() => this.menuService.displayMenuLabel());

  protected readonly menuTooltip = computed(() =>
    !this.menuService.displayMenuLabel() ? 'main-menu.show-labels' : 'main-menu.hide-labels',
  );

  protected readonly helpUrl = computed(() =>
    this.userService.isFidal() ? this.urls.fidalTutoUrl : this.linksService.helpUrl,
  );
  protected readonly menuItemHelp: Signal<MenuItem[]> = computed(() => {
    const hasDriverAppMenu = this.userService.checkAccessRights(this.appRoutes.VirtuarlPrinterApp.root);
    const menus: MenuItem[] = this.userService.isFidal()
      ? [
          {
            label: 'core.header.video-tuto',
            icon: 'video_library',
            href: this.urls.fidalTutoUrl,
          },
          {
            label: 'core.header.make-request',
            icon: 'assignment',
            href: this.urls.fidalAskUrl,
          },
          {
            label: 'core.header.incident',
            icon: 'error',
            href: this.urls.fidalIncidentUrl,
            hasDivider: hasDriverAppMenu,
          },
        ]
      : [
          {
            label: 'core.header.video-tuto',
            icon: 'video_library',
            click: () => this.openVideoModal(),
          },
          {
            label: 'core.header.faq',
            icon: 'help',
            href: this.helpUrl(),
          },
          {
            label: 'core.header.send-request',
            icon: 'support_agent',
            href: this.urls.supportPortalUrl,
            show: () => !this.userService.isOxyane() || this.userService.isSuperUser(),
          },
          {
            label: '0 825 025 000',
            icon: 'phone',
            subtitle: 'core.header.price',
            href: 'tel:0825025000',
            hasDivider: hasDriverAppMenu,
            show: () => !this.userService.isOxyane() || this.userService.isSuperUser(),
          },
        ];

    if (hasDriverAppMenu) {
      menus.push({
        label: 'core.header.printer-driver-app-help',
        subtitle: 'core.header.get-printer-driver-app-help',
        icon: 'help_outline',
        routerLink: this.appRoutes.VirtuarlPrinterApp.Help,
      });
    }
    return menus;
  });

  protected readonly menuItemUser: MenuItem[] = [
    {
      label: 'core.header.Profil',
      icon: 'person',
      routerLink: this.appRoutes.Profil,
      subtitle: 'core.header.p_detail',
      hasDivider: true,
    },
    {
      label: 'core.header.signout',
      icon: 'logout',
      click: () => this.userService.doLogout(), // Exécute la fonction de déconnexion
    },
  ];

  ngOnInit() {
    this.showMenuToggler$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.authentificationService.isLoggedIn() && !this.isMinimalLayoutPage),
      takeUntil(this.destroy$),
    );
  }

  protected toggleMenu() {
    this.menuService.toggleMenuAndLabel();
  }

  protected get IsProdEnv(): boolean {
    return this.envName === 'pro';
  }

  protected openImpersonationModal() {
    this.modalService
      .open(ImpersonationModalComponent, { size: '500px' })
      .closed.pipe(
        take(1),
        filter((changed) => !!changed),
      )
      .subscribe(() => this.router.navigateByUrl(this.appRoutes.Welcome));
  }

  private get isMinimalLayoutPage(): boolean {
    return this.router.url === this.appRoutes.Welcome || this.router.url.startsWith(this.appRoutes.Auth.root);
  }

  private openVideoModal(): void {
    this.modalService.open(VideoModalComponent, {
      data: environment.tutoVideoUrl,
      header: 'core.header.video-tuto',
      size: 'lg',
    });
  }
}
